import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import RectangleButton from 'particles/Button/RectangleButton'
import {
    REVIEW_MODAL_TYPE,
    selectIsMultiSelectMode,
    selectIsPartiposterTagsChipOpen,
    selectIsVoteTypeChipOpen,
    selectPartiposterTagFilter,
    selectVoteTypeFilter,
    setPartiposterTagFilter,
    setReviewModalOpen,
    setReviewModalPartiposterTagFilterOptions,
    setReviewModalType,
    setReviewModalVoteTypeFilterOptions,
    setSubmissionChips,
    setVoteTypeFilter,
    selectResubmittedTypeFilter,
    setReviewModalResubmittedTypeFilterOptions,
    selectIsResubmittedTypeChipOpen,
    setResubmittedTypeFilter,
} from 'state/review'
import { SubmissionFilterChipContainer } from './style'
import Chip from 'particles/Chip'
import Menu from 'particles/Menu'
import MenuItem from 'particles/MenuItem'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { TagOption, selectSortedPartiposterTagsFilter } from 'state/constants'
import { EmptySearchResult } from 'particles/MenuItem/Search'
import { useAddCustomizeFilter } from 'hooks/displayTooltip'
import { selectSubmissionFilters } from 'state/filter'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'

interface FilterOption {
    label: string
    value: string | boolean
}

const getVoteTypeFilterOptions = (t: TFunction): FilterOption[] => {
    return [
        {
            label: t('submission.review.suggestApproveFilter'),
            value: 'approve',
        },
        {
            label: t('submission.review.suggestEditFilter'),
            value: 'request_edits',
        },
        {
            label: t('submission.review.suggestRejectFilter'),
            value: 'reject',
        },
        {
            label: t('submission.review.commentedFilter'),
            value: 'commented',
        },
        {
            label: t('submission.review.notVotedByAnyoneFilter'),
            value: 'not_voted',
        },
        {
            label: t('submission.review.notVotedByMeFilter'),
            value: 'not_voted_by_me',
        },
    ]
}

const getResubmittedTypeFilterOptions = (t: TFunction): FilterOption[] => {
    return [
        {
            label: t('submission.table.submissionTypeLabel.newSubmission'),
            value: false,
        },
        {
            label: t('submission.table.submissionTypeLabel.resubmittedSubmission'),
            value: true,
        },
    ]
}

interface ChipFilterProps<T> {
    id?: string
    chipLabel: string
    setReviewModalFilter: (newOptions: T[]) => void
    setFilter: (newOptions: T[]) => void
    onDeleteChip: () => void
    options: T[]
    getLabel: (option: T) => string
    getCompareValue: (option: T) => string | boolean
    selectedOptions: T[]
    hasSearch?: boolean
}

const ChipFilter = <T,>({
    id,
    chipLabel,
    onDeleteChip,
    setReviewModalFilter,
    setFilter,
    options,
    getLabel,
    getCompareValue,
    selectedOptions,
    hasSearch,
}: ChipFilterProps<T>) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const [items, setItems] = useState(options)
    const isMultiSelectMode = useSelector(selectIsMultiSelectMode)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const handleOnClickChip = (e: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(e.currentTarget)
    }

    const handleOnCloseChip = () => {
        setAnchorEl(null)
        handleClearSearch()
    }

    const handleSearch = (value: string) => {
        const newItems = options ? [...options] : []
        setItems(newItems.filter(item => getLabel(item).toLowerCase().includes(value.toLowerCase())))
    }
    const handleClearSearch = () => {
        setItems(options)
    }

    const getChipLabel = () => {
        let options = ''
        if (selectedOptions?.length > 0) {
            options = ': ' + selectedOptions.map(item => getLabel(item)).join(', ')
        }
        return `${chipLabel}${options}`
    }

    const handleOnChange = (option: T) => {
        const selectedIndex = selectedOptions.findIndex(item => getCompareValue(item) === getCompareValue(option))
        const newFilters =
            selectedIndex === -1
                ? [...selectedOptions, option]
                : selectedOptions.filter(item => getCompareValue(item) !== getCompareValue(option))

        if (isMultiSelectMode) {
            dispatch(setReviewModalType(REVIEW_MODAL_TYPE.FILTER))
            setReviewModalFilter(newFilters)
            dispatch(setReviewModalOpen(true))
        } else {
            setFilter(newFilters)
        }
    }

    const handleDeleteChip = () => {
        if (isMultiSelectMode) {
            dispatch(setReviewModalType(REVIEW_MODAL_TYPE.FILTER))
            setReviewModalFilter([])
            dispatch(setReviewModalOpen(true))
        } else {
            onDeleteChip()
            setFilter([])
        }
    }

    return (
        <>
            <Chip id={id} label={getChipLabel()} onDelete={handleDeleteChip} onClick={handleOnClickChip} />
            <Menu size="s" open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleOnCloseChip}>
                {hasSearch && (
                    <MenuItem.Search
                        onSearch={handleSearch}
                        onClearSearch={handleClearSearch}
                        placeholder={t('placeholderText.genericSearch')}
                    />
                )}
                <MenuItem.Title text={t('dropdownMenu.filterByValueHeader')} case="uppercase" />

                {items && items.length > 0 ? (
                    items.map((item, index: number) => (
                        <MenuItem.Checkbox
                            key={`filter-sort-${index}`}
                            tabIndex={index}
                            onClick={() => {
                                handleOnChange(item)
                                if (isMultiSelectMode) {
                                    handleOnCloseChip()
                                }
                            }}
                            text={getLabel(item)}
                            selected={
                                selectedOptions.findIndex(
                                    option => getCompareValue(option) === getCompareValue(item),
                                ) !== -1
                            }
                        />
                    ))
                ) : (
                    <EmptySearchResult />
                )}
            </Menu>
        </>
    )
}

/**
 * Submission filter currently supports vote_types and partiposter tags
 */
export const SubmissionFilter: React.FC = () => {
    const [t] = useTranslation()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const submissionFilters = useSelector(selectSubmissionFilters)
    const [handleOnClickVoteAndComment, handleOnClickPartiposterTags, handleOnClickResubmittedType] =
        useAddCustomizeFilter()

    const menuItems = useMemo(() => {
        const result = []
        for (const key in submissionFilters) {
            switch (key) {
                case FIELDS_TYPE.VOTE_TYPE:
                    result.push(
                        <MenuItem.Text
                            onClick={() => handleOnClickVoteAndComment(handleClose)}
                            text={t('submission.table.voteAndCommentLabel')}
                        />,
                    )
                    break
                case FIELDS_TYPE.PARTIPOSTER_TAGS:
                    result.push(
                        <MenuItem.Text
                            onClick={() => handleOnClickPartiposterTags(handleClose)}
                            text={t('submission.table.partiposterTagsLabel')}
                        />,
                    )
                    break
                case FIELDS_TYPE.RESUBMITTED:
                    result.push(
                        <MenuItem.Text
                            onClick={() => handleOnClickResubmittedType(handleClose)}
                            text={t('submission.table.submissionTypeLabel')}
                        />,
                    )
                    break
            }
        }
        return result
    }, [submissionFilters, handleOnClickVoteAndComment, handleOnClickPartiposterTags, handleOnClickResubmittedType])

    const handleFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    return menuItems.length > 0 ? (
        <>
            <RectangleButton
                size="s"
                icon="funnel"
                label={t('button.filter')}
                form="text"
                color="neutral"
                onClick={handleFilter}
            />
            <Menu size="s" width={226} open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleClose}>
                <MenuItem.Title text={t('dropdownMenu.filterByHeader')} case="uppercase" />
                {menuItems}
            </Menu>
        </>
    ) : (
        <></>
    )
}

const ResubmittedTypeFilterChip: React.FC = () => {
    const [t] = useTranslation()
    const dispatch = useDispatch()

    const selectedOptions = useSelector(selectResubmittedTypeFilter)
    const submissionFilters = useSelector(selectSubmissionFilters)
    const isPartiposterTagsChipOpen = useSelector(selectIsPartiposterTagsChipOpen)
    const isVoteTypeChipOpen = useSelector(selectIsVoteTypeChipOpen)
    const resubmittedTypeOptions = getResubmittedTypeFilterOptions(t)

    const options = useMemo(() => {
        const result = [] as FilterOption[]
        const filter = submissionFilters[FIELDS_TYPE.RESUBMITTED]
        resubmittedTypeOptions.forEach(submissionType => {
            if (filter && filter.includes(submissionType.value as string)) {
                result.push(submissionType)
            }
        })
        return result
    }, [submissionFilters, resubmittedTypeOptions])

    return (
        <ChipFilter
            id="submission-filter-resubmitted"
            chipLabel={t('submission.table.submissionTypeLabel')}
            onDeleteChip={() => {
                dispatch(
                    setSubmissionChips({
                        isResubmittedTypeChipOpen: false,
                        isVoteTypeChipOpen,
                        isPartiposterTagsChipOpen,
                    }),
                )
            }}
            options={options}
            setReviewModalFilter={newFilters => {
                dispatch(setReviewModalResubmittedTypeFilterOptions(newFilters))
            }}
            setFilter={newFilters => {
                dispatch(setResubmittedTypeFilter(newFilters))
            }}
            getLabel={option => option.label}
            getCompareValue={option => option.value}
            selectedOptions={selectedOptions}
        />
    )
}

const VoteFilterChip: React.FC = () => {
    const [t] = useTranslation()
    const dispatch = useDispatch()

    const selectedOptions = useSelector(selectVoteTypeFilter)
    const submissionFilters = useSelector(selectSubmissionFilters)
    const isPartiposterTagsChipOpen = useSelector(selectIsPartiposterTagsChipOpen)
    const isResubmittedTypeChipOpen = useSelector(selectIsResubmittedTypeChipOpen)
    const voteTypesOptions = getVoteTypeFilterOptions(t)

    const options = useMemo(() => {
        const result = [] as FilterOption[]
        const filter = submissionFilters[FIELDS_TYPE.VOTE_TYPE]
        voteTypesOptions.forEach(voteType => {
            if (filter && filter.includes(voteType.value as string)) {
                result.push(voteType)
            }
        })
        return result
    }, [submissionFilters, voteTypesOptions])

    return (
        <ChipFilter
            id="submission-filter-vote-and-comment"
            chipLabel={t('submission.table.voteAndCommentLabel')}
            onDeleteChip={() => {
                dispatch(
                    setSubmissionChips({
                        isVoteTypeChipOpen: false,
                        isPartiposterTagsChipOpen,
                        isResubmittedTypeChipOpen,
                    }),
                )
            }}
            options={options}
            setReviewModalFilter={newFilters => {
                dispatch(setReviewModalVoteTypeFilterOptions(newFilters))
            }}
            setFilter={newFilters => {
                dispatch(setVoteTypeFilter(newFilters))
            }}
            getLabel={option => option.label}
            getCompareValue={option => option.value}
            selectedOptions={selectedOptions}
        />
    )
}

const PartiposterTagFilterChip: React.FC = () => {
    const [t] = useTranslation()
    const dispatch = useDispatch()

    const partiposterTags = useSelector(selectSortedPartiposterTagsFilter)
    const submissionFilters = useSelector(selectSubmissionFilters)
    const selectedOptions = useSelector(selectPartiposterTagFilter)
    const isVoteTypeChipOpen = useSelector(selectIsVoteTypeChipOpen)
    const isResubmittedTypeChipOpen = useSelector(selectIsResubmittedTypeChipOpen)

    const options = useMemo(() => {
        const result = [
            {
                uuid: 'null',
                label: t('submission.tags.noTagYetFilteringOption'),
                value: 'null',
                enum: 'no tag',
                category: 'null',
            },
        ] as TagOption[]
        const filter = submissionFilters[FIELDS_TYPE.PARTIPOSTER_TAGS]
        partiposterTags.forEach(tag => {
            if (filter && filter.includes(tag.uuid)) {
                result.push({
                    uuid: tag.uuid,
                    label: tag.name,
                    value: tag.uuid,
                    enum: tag.name,
                    category: tag.category.uuid,
                })
            }
        })

        return result
    }, [submissionFilters, partiposterTags])

    return (
        <ChipFilter
            id="submission-filter-partiposter-tags"
            chipLabel={t('submission.table.partiposterTagsLabel')}
            onDeleteChip={() => {
                dispatch(
                    setSubmissionChips({
                        isResubmittedTypeChipOpen,
                        isVoteTypeChipOpen,
                        isPartiposterTagsChipOpen: false,
                    }),
                )
            }}
            setReviewModalFilter={newFilters => {
                dispatch(setReviewModalPartiposterTagFilterOptions(newFilters))
            }}
            setFilter={newFilters => {
                dispatch(setPartiposterTagFilter(newFilters))
            }}
            options={options}
            getLabel={option => option.label}
            getCompareValue={option => option.uuid}
            selectedOptions={selectedOptions}
            hasSearch
        />
    )
}

interface SubmissionFilterChipProps {
    className?: string
}
export const SubmissionFilterChip = ({ className }: SubmissionFilterChipProps) => {
    const isVoteTypeChipOpen = useSelector(selectIsVoteTypeChipOpen)
    const isPartiposterTagsChipOpen = useSelector(selectIsPartiposterTagsChipOpen)
    const isResubmittedTypeChipOpen = useSelector(selectIsResubmittedTypeChipOpen)

    const isFilterChipVisible = isVoteTypeChipOpen || isPartiposterTagsChipOpen || isResubmittedTypeChipOpen

    return isFilterChipVisible ? (
        <SubmissionFilterChipContainer className={className}>
            {isVoteTypeChipOpen && <VoteFilterChip />}
            {isPartiposterTagsChipOpen && <PartiposterTagFilterChip />}
            {isResubmittedTypeChipOpen && <ResubmittedTypeFilterChip />}
        </SubmissionFilterChipContainer>
    ) : null
}
